import { memo, useEffect, useRef, useState } from 'react';

// Single fixed Q&A pair shown in the hero demo (illustrative, not real user data).
// One pair, forever — swapping copy mid-loop would change bubble geometry.
const DEMO = {
  question: "Walk me through how you'd design a rate limiter for our public API.",
  answerWords:
    "I'd start with the requirements — limit per user, burst tolerance — then a token-bucket per key in Redis…".split(
      ' ',
    ),
  meters: [
    { label: 'Clarity', value: 88 },
    { label: 'Structure', value: 79 },
    { label: 'Depth', value: 82 },
  ],
  score: 84,
  scoreFrom: 62, // count-up starts here — never shows a "failing" number
  tip: "Tip: quantify limits — e.g. '100 req/min per key'.",
  context: 'Answer 2 of 6',
};

const TICK_MS = 90;
const QUESTION_CHAR_MS = 1000 / 30; // ~30 chars/s
const ANSWER_WORD_MS = 130;
const METER_FILL_MS = 700;
const METER_STAGGER_MS = 120;
const SCORE_COUNT_MS = 900;

// Phase boundaries are computed from string lengths so copy edits can't desync the timeline.
const T = (() => {
  const typeStart = 500; // phase A: typing dots
  const typeEnd = typeStart + Math.round(DEMO.question.length * QUESTION_CHAR_MS); // phase B: question types
  const answerRise = typeEnd + 500; // human beat, then phase C: candidate row rises, equalizer runs
  const streamStart = answerRise + 700; // phase D: answer streams word by word
  const streamEnd = streamStart + DEMO.answerWords.length * ANSWER_WORD_MS;
  const feedbackAt = streamEnd + 300; // feedback card rises
  const scoreStart = feedbackAt + 100; // score counts up, meters fill
  const tipAt = feedbackAt + 1300; // tip row rises, then the long hold
  const sheenAt = tipAt + 1100; // sheen fires exactly once during the hold
  const fadeStart = tipAt + 4700; // conversation content fades, loop resets
  const loopMs = fadeStart + 500;
  return {
    typeStart,
    typeEnd,
    answerRise,
    streamStart,
    streamEnd,
    feedbackAt,
    scoreStart,
    tipAt,
    sheenAt,
    fadeStart,
    loopMs,
  };
})();

// Sentinel: first paint (and the reduced-motion / no-JS state) IS the finished composition.
const FINISHED = Number.MAX_SAFE_INTEGER;

const easeOut = (p: number) => 1 - Math.pow(1 - p, 3);
const progress = (t: number, from: number, durationMs: number) =>
  Math.min(1, Math.max(0, (t - from) / durationMs));

export const HeroDemoPanel = memo(function HeroDemoPanel() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [elapsed, setElapsed] = useState(FINISHED);

  // Loop driver — the interval runs only while motion is allowed, the panel is
  // on screen (IntersectionObserver, threshold 0.3) and the tab is visible.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let intervalId: number | null = null;
    let intersecting = false;

    const stop = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const sync = () => {
      const canRun =
        intersecting && !reduceMotion.matches && document.visibilityState === 'visible';
      if (canRun && intervalId === null) {
        intervalId = window.setInterval(() => {
          // First qualifying tick crossfades the finished frame out (fade phase), then wraps to 0.
          setElapsed((e) => (e === FINISHED ? T.fadeStart : (e + TICK_MS) % T.loopMs));
        }, TICK_MS);
      } else if (!canRun) {
        stop();
      }
    };
    const onReduceChange = () => {
      if (reduceMotion.matches) setElapsed(FINISHED); // snap to the finished frame
      sync();
    };
    const onVisibility = () => sync();
    const observer = new IntersectionObserver(
      (entries) => {
        intersecting = entries.some((entry) => entry.isIntersecting);
        sync();
      },
      { threshold: 0.3 },
    );

    observer.observe(root);
    reduceMotion.addEventListener('change', onReduceChange);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      observer.disconnect();
      reduceMotion.removeEventListener('change', onReduceChange);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Pointer parallax — translate-only, CSS custom props, no React state.
  // Attaches only for fine pointers without reduced motion.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId = 0;
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let pointerActive = false;

    const step = () => {
      currentX += (targetX - currentX) * 0.08;
      currentY += (targetY - currentY) * 0.08;
      if (!pointerActive && Math.abs(currentX) < 0.001 && Math.abs(currentY) < 0.001) {
        root.style.setProperty('--hr-px', '0');
        root.style.setProperty('--hr-py', '0');
        rafId = 0;
        return;
      }
      root.style.setProperty('--hr-px', currentX.toFixed(4));
      root.style.setProperty('--hr-py', currentY.toFixed(4));
      rafId = requestAnimationFrame(step);
    };
    const ensureLoop = () => {
      if (rafId === 0) rafId = requestAnimationFrame(step);
    };
    const onMouseMove = (e: MouseEvent) => {
      const rect = root.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      targetY = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      pointerActive = true;
      ensureLoop();
    };
    const onMouseLeave = () => {
      targetX = 0;
      targetY = 0;
      pointerActive = false;
      ensureLoop(); // lerp back to 0, then the loop cancels itself
    };

    root.addEventListener('mousemove', onMouseMove);
    root.addEventListener('mouseleave', onMouseLeave);
    return () => {
      root.removeEventListener('mousemove', onMouseMove);
      root.removeEventListener('mouseleave', onMouseLeave);
      if (rafId !== 0) cancelAnimationFrame(rafId);
    };
  }, []);

  // All scene state is derived from `elapsed` against DEMO at render.
  const finished = elapsed === FINISHED;
  const t = finished ? T.tipAt + 1 : elapsed; // "everything settled, nothing fading" frame

  const dotsOn = !finished && t < T.typeStart;
  const qChars = Math.min(
    DEMO.question.length,
    Math.max(0, Math.floor((t - T.typeStart) / QUESTION_CHAR_MS)),
  );
  const caretOn = !finished && t >= T.typeStart && t < T.typeEnd;
  const answerOn = t >= T.answerRise;
  const eqOn = !finished && t >= T.answerRise && t < T.streamStart;
  const wCount =
    t < T.streamStart
      ? 0
      : Math.min(DEMO.answerWords.length, Math.floor((t - T.streamStart) / ANSWER_WORD_MS) + 1);
  const feedbackOn = t >= T.feedbackAt;
  const metersOn = t >= T.scoreStart;
  const tipOn = t >= T.tipAt;
  const sheenOn = !finished && t >= T.sheenAt && t < T.sheenAt + 1300;
  const contentFading = !finished && t >= T.fadeStart;
  const speaker: 'ai' | 'user' =
    !finished && t >= T.answerRise && t < T.fadeStart ? 'user' : 'ai';

  const scoreDisplay = feedbackOn
    ? Math.round(
        DEMO.scoreFrom + (DEMO.score - DEMO.scoreFrom) * easeOut(progress(t, T.scoreStart, SCORE_COUNT_MS)),
      )
    : DEMO.scoreFrom;
  const meterDisplay = (i: number, value: number) =>
    metersOn
      ? Math.round(value * easeOut(progress(t, T.scoreStart + i * METER_STAGGER_MS, METER_FILL_MS)))
      : 0;

  // Two in-flow spans (visible + invisible remainder) keep bubble geometry identical
  // to the finished text at every frame — no absolute overlays, no font-metric fragility.
  const questionVisible = DEMO.question.slice(0, qChars);
  const questionHidden = DEMO.question.slice(qChars);
  const answerVisible = DEMO.answerWords.slice(0, wCount).join(' ');
  const answerHiddenRaw = DEMO.answerWords.slice(wCount).join(' ');
  const answerHidden = wCount > 0 && answerHiddenRaw ? ` ${answerHiddenRaw}` : answerHiddenRaw;

  return (
    <div ref={rootRef} className="hr-rise relative min-w-0" style={{ animationDelay: '220ms' }}>
      <div className="relative">
        {/* speaker-reactive backdrop glow — blur is static, only wrapper transform/opacity move */}
        <div aria-hidden className="hr-breathe pointer-events-none absolute -inset-5 -z-10">
          <div
            className="absolute inset-0 rounded-[2rem] bg-gradient-to-tr from-stitch-accent/25 via-stitch-accent/10 to-transparent blur-2xl transition-opacity duration-[800ms]"
            style={{ opacity: speaker === 'ai' ? 1 : 0.35 }}
          />
          <div
            className="absolute inset-0 rounded-[2rem] bg-gradient-to-tr from-stitch-primary/25 via-stitch-primary/10 to-transparent blur-2xl transition-opacity duration-[800ms]"
            style={{ opacity: speaker === 'user' ? 1 : 0.35 }}
          />
        </div>

        {/* animated card — decorative; the left column carries the message for screen readers */}
        <div
          aria-hidden="true"
          style={{
            transform: 'translate3d(calc(var(--hr-px, 0) * 5px), calc(var(--hr-py, 0) * 4px), 0)',
          }}
        >
          <div className="relative overflow-hidden rounded-stitch-2xl border border-stitch-border bg-stitch-surface shadow-2xl">
            {/* window chrome */}
            <div className="flex items-center gap-2 border-b border-stitch-border bg-stitch-surface-2 px-4 py-3">
              <span className="h-3 w-3 rounded-full bg-stitch-danger/70" />
              <span className="h-3 w-3 rounded-full bg-stitch-warning/70" />
              <span className="h-3 w-3 rounded-full bg-stitch-success/70" />
              <span className="ml-2 truncate text-xs font-medium text-stitch-muted">
                AI Mock Interview · Frontend Engineer
              </span>
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-stitch-fg-subtle">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-stitch-accent" />
                Demo
              </span>
            </div>

            {/* conversation — everything stays mounted and in flow; geometry never changes */}
            <div
              className="space-y-4 p-5 [contain:layout_paint]"
              style={{
                opacity: contentFading ? 0 : 1,
                transition: contentFading
                  ? 'opacity 500ms ease-in'
                  : 'opacity 300ms var(--stitch-ease-out)',
              }}
            >
              <div className="flex gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-stitch-accent-soft text-stitch-accent">
                  <span
                    className="material-symbols-outlined text-lg"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    auto_awesome
                  </span>
                </div>
                <div className="relative max-w-[82%] rounded-2xl rounded-tl-sm bg-stitch-surface-2 px-4 py-3 text-sm leading-relaxed text-stitch-fg">
                  {/* typing dots overlay the first (still invisible) line — zero layout impact */}
                  <span
                    className="absolute left-4 top-3 flex h-[1.625em] items-center gap-1"
                    style={{ opacity: dotsOn ? 1 : 0, transition: 'opacity 200ms ease' }}
                  >
                    {[0, 150, 300].map((delay) => (
                      <span
                        key={delay}
                        className={`h-1.5 w-1.5 rounded-full bg-stitch-muted${dotsOn ? ' hr-dot' : ''}`}
                        style={{ animationDelay: `${delay}ms` }}
                      />
                    ))}
                  </span>
                  <span>{questionVisible}</span>
                  {/* negative margin cancels the caret's inline footprint so wrapping never shifts */}
                  <span
                    className={`-mr-[3px] ml-px inline-block h-[1.1em] w-0.5 translate-y-[2px] bg-stitch-accent transition-opacity duration-200 ${
                      caretOn ? 'hr-caret' : 'opacity-0'
                    }`}
                  />
                  <span className="invisible">{questionHidden}</span>
                </div>
              </div>

              <div
                className="flex flex-row-reverse gap-3"
                style={{
                  opacity: answerOn ? 1 : 0,
                  transform: answerOn ? 'none' : 'translateY(8px)',
                  transition:
                    'opacity 300ms var(--stitch-ease-out), transform 300ms var(--stitch-ease-out)',
                }}
              >
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-stitch-primary text-white">
                  <span className="material-symbols-outlined text-lg">person</span>
                </div>
                <div className="relative max-w-[82%] rounded-2xl rounded-tr-sm bg-stitch-primary px-4 py-3 text-sm leading-relaxed text-white">
                  <span
                    className="pointer-events-none absolute inset-0 flex items-center justify-center gap-[3px]"
                    style={{ opacity: eqOn ? 1 : 0, transition: 'opacity 200ms ease' }}
                  >
                    {[0, 120, 240, 360].map((delay) => (
                      <span
                        key={delay}
                        className={`h-3.5 w-[3px] rounded-full bg-white/90${eqOn ? ' hr-eq' : ''}`}
                        style={{ animationDelay: `${delay}ms` }}
                      />
                    ))}
                  </span>
                  <span>{answerVisible}</span>
                  <span className="invisible">{answerHidden}</span>
                </div>
              </div>

              {/* live feedback card */}
              <div
                className="rounded-stitch-xl border border-stitch-border bg-stitch-canvas p-4"
                style={{
                  opacity: feedbackOn ? 1 : 0,
                  transform: feedbackOn ? 'none' : 'translateY(12px)',
                  transition:
                    'opacity 400ms var(--stitch-ease-out), transform 400ms var(--stitch-ease-out)',
                }}
              >
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-stitch-muted">
                    Instant feedback
                  </p>
                  <span className="flex items-center gap-2">
                    <span className="text-[11px] text-stitch-muted">{DEMO.context}</span>
                    <span className="rounded-full bg-stitch-success-soft px-2.5 py-0.5 text-xs font-bold tabular-nums text-stitch-success">
                      Score {scoreDisplay}
                    </span>
                  </span>
                </div>
                <div className="space-y-2.5">
                  {DEMO.meters.map((m, i) => (
                    <div key={m.label} className="flex items-center gap-3">
                      <span className="w-16 flex-shrink-0 text-xs font-medium text-stitch-fg-soft">
                        {m.label}
                      </span>
                      <span className="h-2 flex-1 overflow-hidden rounded-full bg-stitch-surface-2">
                        <span
                          className="block h-full w-full origin-left rounded-full bg-gradient-to-r from-stitch-primary to-stitch-accent"
                          style={{
                            transform: `scaleX(${metersOn ? m.value / 100 : 0})`,
                            transition: `transform ${METER_FILL_MS}ms var(--stitch-ease-out)`,
                            transitionDelay: `${i * METER_STAGGER_MS}ms`,
                          }}
                        />
                      </span>
                      <span className="w-7 flex-shrink-0 text-right text-xs font-semibold tabular-nums text-stitch-fg">
                        {meterDisplay(i, m.value)}
                      </span>
                    </div>
                  ))}
                </div>
                <div
                  className="mt-3 flex items-start gap-1.5 border-t border-stitch-border pt-2.5"
                  style={{
                    opacity: tipOn ? 1 : 0,
                    transform: tipOn ? 'none' : 'translateY(8px)',
                    transition:
                      'opacity 250ms var(--stitch-ease-out), transform 250ms var(--stitch-ease-out)',
                  }}
                >
                  <span
                    className="material-symbols-outlined text-base text-stitch-accent"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    lightbulb
                  </span>
                  <p className="text-xs text-stitch-fg-soft">{DEMO.tip}</p>
                </div>
              </div>
            </div>

            {/* sheen — fires exactly once per loop during the hold, desktop only */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 hidden overflow-hidden rounded-stitch-2xl lg:block"
            >
              {sheenOn && (
                <div className="hr-sheen absolute -inset-y-4 w-1/3 rotate-[20deg] bg-gradient-to-r from-transparent via-white/[0.07] to-transparent dark:via-white/[0.04]" />
              )}
            </div>

            {/* playhead — driven by the same tick, hidden in the static fallback */}
            {!finished && (
              <div className="absolute inset-x-0 bottom-0 h-0.5 bg-stitch-border/60">
                <div
                  className="h-full origin-left bg-gradient-to-r from-stitch-primary to-stitch-accent"
                  style={{ transform: `scaleX(${t / T.loopMs})` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* floating chip — outer div takes the parallax counter-translate, inner div floats */}
        <div
          className="absolute -bottom-5 -left-5 hidden md:block"
          style={{
            transform: 'translate3d(calc(var(--hr-px, 0) * -8px), calc(var(--hr-py, 0) * -6px), 0)',
          }}
        >
          <div className="hr-float-slow flex items-center gap-3 rounded-stitch-xl border border-stitch-border bg-stitch-surface p-3 shadow-stitch-pop">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-stitch-primary-soft text-stitch-primary">
              <span className="material-symbols-outlined">groups</span>
            </div>
            <div>
              <p className="text-sm font-bold text-stitch-fg">Or book a real interviewer</p>
              <p className="text-xs text-stitch-muted">1:1 sessions with experts</p>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-3 text-center text-xs text-stitch-muted">
        Illustrative demo — real sessions adapt to your answers.
      </p>
    </div>
  );
});
